import { TextInput, TextInputProps } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form/lib/use-form"
import React, { useContext } from "react"
import NumberFormat from "react-number-format"
import { EditingContext } from "../editingContext"
import { KeysOfType } from "~/utils/types"
import { ReadOnlyInput } from "./ReadOnlyInput"
import { Prisma } from "@prisma/client"

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "CAD",
  currencyDisplay: "narrowSymbol",
})

// Ensures that the form values can be both Decimal _and_ null
type NumberInputProps<
  T extends Record<K, Prisma.Decimal | null>,
  K extends KeysOfType<T, Prisma.Decimal> & KeysOfType<T, null>
> = Omit<TextInputProps, "form" | "defaultValue" | "type" | "value" | "error"> & {
  form: UseFormReturnType<T>
  field: K
}

export const NumberInput = <
  T extends Record<K, Prisma.Decimal | null>,
  K extends KeysOfType<T, Prisma.Decimal> & KeysOfType<T, null>
>({
  form,
  field,
  onBlur,
  ...props
}: NumberInputProps<T, K>) => {
  const editing = useContext(EditingContext)

  const value = form.values[field] // TypeScript can't check for null with the array access for some reason

  return editing ? (
    <NumberFormat
      value={value?.toString()}
      onValueChange={({ value }) =>
        form.setFieldValue(field, (value ? new Prisma.Decimal(value) : null) as T[K])
      }
      error={form.errors[field]}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        form.validateField(field)
        onBlur && onBlur(event)
      }}
      thousandSeparator
      prefix="$"
      allowNegative={false}
      customInput={TextInput}
      {...props}
    />
  ) : (
    <ReadOnlyInput
      value={value !== null ? moneyFormatter.format(value.toNumber()) : null}
      error={field in form.errors}
      {...props}
    />
  )
}

export const NumberOutput = ({
  value,
  fallback = "",
}: {
  value: Prisma.Decimal | null
  fallback?: string
}) => (
  <>
    {value ? (
      <NumberFormat
        value={value.toString()}
        thousandSeparator
        prefix="$"
        decimalScale={2}
        fixedDecimalScale
        displayType="text"
      />
    ) : (
      fallback
    )}
  </>
)
